// File: e1c/src/components/content_editor/PainMapBlockEditor.js
// REFACTORED: This version is a fully controlled component, removing internal state
// and the debounce logic to resolve input responsiveness and data sync issues.
// FIX: Added 'Button' to the MUI import list to resolve build error.

import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
    TextField, Box, Typography, Select, MenuItem, FormControl, InputLabel,
    Checkbox, FormControlLabel, Paper, Grid, Tooltip, Slider,
    Dialog, DialogActions, DialogContent, DialogTitle, Button
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

const bodyImages = [
    { id: 'anterior', label: 'Anterior View', url: '/images/body/anterior_view.svg' },
    { id: 'posterior', label: 'Posterior View', url: '/images/body/posterior_view.svg' },
];

const PainMapBlockEditor = ({ block, onUpdateBlock, onFocus, onBlur }) => {
    // --- NO MORE INTERNAL STATE FOR PROPS ---
    const svgContainerRef = useRef(null);
    
    // State is now only for the modal's temporary editing context
    const [isIntensityModalOpen, setIsIntensityModalOpen] = useState(false);
    const [currentPointForIntensity, setCurrentPointForIntensity] = useState(null);
    const [currentIntensity, setCurrentIntensity] = useState(block.options?.painScaleMax / 2 || 5);
    const [currentPointLabel, setCurrentPointLabel] = useState('');

    const memoizedOptions = useMemo(() => ({
        imageUrl: block.options?.imageUrl || bodyImages[0].url,
        painScaleMin: block.options?.painScaleMin ?? 0,
        painScaleMax: block.options?.painScaleMax ?? 10,
        painScaleStep: block.options?.painScaleStep ?? 1,
        allowMultiplePoints: block.options?.allowMultiplePoints !== undefined ? block.options.allowMultiplePoints : true,
        points: block.options?.points || [],
    }), [block.options]);

    // Handlers now directly call the parent's update function
    const handleUpdate = useCallback((updatePayload) => {
        if (onUpdateBlock) {
            onUpdateBlock(updatePayload);
        }
    }, [onUpdateBlock]);

    const handleImageClick = (event) => {
        if (!svgContainerRef.current) return;
        if (!memoizedOptions.allowMultiplePoints && memoizedOptions.points.length >= 1) {
            alert("Multiple points are not allowed. Please remove an existing point to add a new one.");
            return;
        }
        const rect = svgContainerRef.current.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;

        const newPointBase = { id: uuidv4(), x, y, label: '' };
        setCurrentPointForIntensity(newPointBase);
        setCurrentIntensity(block.options?.painScaleDefault ?? Math.round(memoizedOptions.painScaleMax / 2));
        setCurrentPointLabel('');
        setIsIntensityModalOpen(true);
    };

    const handleSavePainPoint = () => {
        let finalPoint;
        let newPointsArray;

        if (currentPointForIntensity) {
             // Check if it's an existing point or a new one
            const isExistingPoint = memoizedOptions.points.some(p => p.id === currentPointForIntensity.id);
            finalPoint = { ...currentPointForIntensity, intensity: currentIntensity, label: currentPointLabel.trim() };

            if (isExistingPoint) {
                newPointsArray = memoizedOptions.points.map(p => p.id === finalPoint.id ? finalPoint : p);
            } else {
                newPointsArray = [...memoizedOptions.points, finalPoint];
            }
            handleUpdate({ options: { points: newPointsArray } });
        }
        handleCloseIntensityModal();
    };
    
    const handleEditPainPoint = (pointId) => {
         const pointToEdit = memoizedOptions.points.find(p => p.id === pointId);
         if (pointToEdit) {
             setCurrentPointForIntensity(pointToEdit);
             setCurrentIntensity(pointToEdit.intensity ?? Math.round(memoizedOptions.painScaleMax / 2));
             setCurrentPointLabel(pointToEdit.label || '');
             setIsIntensityModalOpen(true);
         }
    };

    const handleCloseIntensityModal = () => {
        setIsIntensityModalOpen(false);
        setCurrentPointForIntensity(null);
        setCurrentPointLabel('');
    };

    const removePainPoint = (pointIdToRemove) => {
        const newPointsArray = memoizedOptions.points.filter(point => point.id !== pointIdToRemove);
        handleUpdate({ options: { points: newPointsArray } });
    };

    return (
        <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>Pain Map Configuration</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label="Prompt / Question"
                        value={block.prompt_text || block.prompt || ''}
                        onChange={(e) => handleUpdate({ prompt: e.target.value })}
                        fullWidth variant="outlined" onFocus={() => onFocus(block.id)} onBlur={onBlur}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label="Instruction Text (Optional)"
                        value={block.instruction_text || ''}
                        onChange={(e) => handleUpdate({ instruction_text: e.target.value })}
                        fullWidth multiline rows={2} variant="outlined" onFocus={() => onFocus(block.id)} onBlur={onBlur}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Body Visual</InputLabel>
                        <Select
                            value={memoizedOptions.imageUrl} label="Body Visual"
                            onChange={(e) => handleUpdate({ options: { imageUrl: e.target.value, points: [] } })}
                        >
                            {bodyImages.map((img) => <MenuItem key={img.id} value={img.url}>{img.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                     <img src={process.env.PUBLIC_URL + memoizedOptions.imageUrl} alt="Body preview" style={{ maxHeight: '100px', border: '1px solid #ccc', borderRadius: '4px' }}/>
                </Grid>
                <Grid item xs={12}><Typography variant="subtitle2" gutterBottom sx={{mt:1}}>Pain Scale Settings</Typography></Grid>
                <Grid item xs={4}>
                    <TextField label="Scale Min" type="number" value={memoizedOptions.painScaleMin}
                        onChange={(e) => handleUpdate({ options: { painScaleMin: parseInt(e.target.value, 10) || 0 }})}
                        fullWidth variant="outlined"
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField label="Scale Max" type="number" value={memoizedOptions.painScaleMax}
                        onChange={(e) => handleUpdate({ options: { painScaleMax: parseInt(e.target.value, 10) || 10 }})}
                        fullWidth variant="outlined"
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField label="Scale Step" type="number" value={memoizedOptions.painScaleStep}
                        onChange={(e) => handleUpdate({ options: { painScaleStep: parseInt(e.target.value, 10) || 1 }})}
                        fullWidth variant="outlined" inputProps={{ min: 1 }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={ <Checkbox checked={memoizedOptions.allowMultiplePoints} onChange={(e) => handleUpdate({ options: { allowMultiplePoints: e.target.checked }})} /> }
                        label="Allow patient to mark multiple pain points"
                    />
                </Grid>
            </Grid>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, mb:1, color: 'secondary.main' }}>Interactive Pain Map Area</Typography>
            <Typography variant="caption" display="block" sx={{mb:1}}>Click on the image below to mark pain points.</Typography>
            <Box ref={svgContainerRef} onClick={handleImageClick}
                sx={{ mt: 1, position: 'relative', maxWidth: '500px', border: '1px dashed grey', cursor: 'crosshair', overflow: 'hidden' }}>
                <img src={process.env.PUBLIC_URL + memoizedOptions.imageUrl} alt="Interactive body visual" style={{ display: 'block', width: '100%', height: 'auto' }} />
                {memoizedOptions.points.map(point => (
                    <Tooltip key={point.id} title={`Pain: ${point.intensity ?? 'N/A'}${point.label ? ' - ' + point.label : ''}. Click to edit/remove.`}>
                        <Box
                            sx={{
                                position: 'absolute', left: `${point.x}%`, top: `${point.y}%`,
                                transform: 'translate(-50%, -50%)', width: 12, height: 12,
                                backgroundColor: 'red', borderRadius: '50%', border: '1px solid white',
                                cursor: 'pointer', opacity: 0.8,
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Edit point "${point.label || 'Unnamed'}"? Cancel to remove.`)) {
                                    handleEditPainPoint(point.id);
                                } else {
                                    removePainPoint(point.id);
                                }
                            }}
                        />
                    </Tooltip>
                ))}
            </Box>
            <Typography variant="caption" display="block" sx={{mt:1}}>{memoizedOptions.points.length} point(s) marked.</Typography>

            <Dialog open={isIntensityModalOpen} onClose={handleCloseIntensityModal} maxWidth="xs" fullWidth>
                <DialogTitle>Set Pain Intensity</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>Rate the pain from {memoizedOptions.painScaleMin} to {memoizedOptions.painScaleMax}.</Typography>
                    <Slider
                        value={currentIntensity} onChange={(e, newValue) => setCurrentIntensity(newValue)}
                        aria-labelledby="pain-intensity-slider" valueLabelDisplay="auto"
                        step={memoizedOptions.painScaleStep} marks
                        min={memoizedOptions.painScaleMin} max={memoizedOptions.painScaleMax}
                        sx={{mt:3, mb:2}}
                    />
                     <TextField
                        label="Point Label (Optional)" value={currentPointLabel}
                        onChange={(e) => setCurrentPointLabel(e.target.value)}
                        fullWidth variant="outlined" size="small"
                        helperText="e.g., Left Knee, Lower Back" sx={{mt:1}}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseIntensityModal}>Cancel</Button>
                    <Button onClick={handleSavePainPoint} color="primary">Save Point</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default PainMapBlockEditor;